import React, { useState } from 'react'
import { useRecordContext, useRefresh, useNotify } from 'react-admin'
import { Typography, Box, Card, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'

import { API_URL } from '../../../services/settings'


const QuickActions = () => {
    const record = useRecordContext()
    const refresh = useRefresh()
    const notify = useNotify()
    const [loading, setLoading] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [selectedAction, setSelectedAction] = useState(null)
    const [meetingDate, setMeetingDate] = useState(new Date().toISOString().slice(0, 16))
    const [proposalAmount, setProposalAmount] = useState('')
    const [contactDate, setContactDate] = useState(new Date().toISOString().slice(0, 16))
    const [contactMethod, setContactMethod] = useState('email')
    const [contactNote, setContactNote] = useState('')

    if (!record) {
        return null
    }

    // Define available actions per status
    // Pipeline: new → contacted → qualified → meeting_scheduled → proposal_sent → converted
    // Terminal states: unqualified, lost
    const statusActions = {
        new: [
            { endpoint: 'mark_contacted', label: 'Mark as Contacted', color: 'primary' },
        ],
        contacted: [
            { endpoint: 'mark_qualified', label: 'Mark as Qualified', color: 'primary' },
            { endpoint: 'mark_unqualified', label: 'Mark as Unqualified', color: 'error' },
            { endpoint: 'mark_nurturing', label: 'Mark as Nurturing', color: 'info' },
            { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
        ],
        qualified: [
            { endpoint: 'mark_meeting_scheduled', label: 'Schedule Meeting', color: 'primary' },
            { endpoint: 'mark_unqualified', label: 'Mark as Unqualified', color: 'error' },
            { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
            { endpoint: 'mark_nurturing', label: 'Back to Nurturing', color: 'info' },
            { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
        ],
        meeting_scheduled: [
            { endpoint: 'mark_proposal_sent', label: 'Send Proposal', color: 'primary' },
            { endpoint: 'mark_unqualified', label: 'Mark as Unqualified', color: 'error' },
            { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
            { endpoint: 'mark_nurturing', label: 'Back to Nurturing', color: 'info' },
            { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
        ],
        proposal_sent: [
            { endpoint: 'mark_converted', label: 'Mark as Converted', color: 'success' },
            { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
            { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
        ],
        nurturing: [
            { endpoint: 'mark_contacted', label: 'Mark as Contacted', color: 'primary' },
            { endpoint: 'mark_qualified', label: 'Mark as Qualified', color: 'primary' },
            { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
            { endpoint: 'mark_on_hold', label: 'Put on Hold', color: 'warning' },
        ],
        on_hold: [
            { endpoint: 'mark_contacted', label: 'Mark as Contacted', color: 'primary' },
            { endpoint: 'mark_qualified', label: 'Mark as Qualified', color: 'primary' },
            { endpoint: 'mark_nurturing', label: 'Back to Nurturing', color: 'info' },
            { endpoint: 'mark_lost', label: 'Mark as Lost', color: 'error' },
        ],
    }

    const actions = statusActions[record.status] || []

    if (actions.length === 0) {
        return null
    }

    const handleStatusChange = async (endpoint, extraData = {}) => {
        setLoading(true)
        try {
            const token = localStorage.getItem('token')
            const response = await fetch(`${API_URL}/leads/${record.id}/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: Object.keys(extraData).length > 0 ? JSON.stringify(extraData) : undefined,
            })
            if (response.ok) {
                notify('Lead status updated', { type: 'success' })
                setDialogOpen(false)
                setSelectedAction(null)
                setContactNote('')
                refresh()
            } else {
                notify('Failed to update lead status', { type: 'error' })
            }
        } catch (e) {
            notify('Error: ' + e.message, { type: 'error' })
        } finally {
            setLoading(false)
        }
    }

    const handleActionClick = (endpoint) => {
        if (endpoint === 'mark_contacted') {
            setSelectedAction(endpoint)
            setContactDate(new Date().toISOString().slice(0, 16))
            setContactMethod('email')
            setContactNote('')
            setDialogOpen(true)
        } else if (endpoint === 'mark_meeting_scheduled') {
            setSelectedAction(endpoint)
            setMeetingDate(new Date().toISOString().slice(0, 16))
            setDialogOpen(true)
        } else if (endpoint === 'mark_proposal_sent') {
            setSelectedAction(endpoint)
            setProposalAmount(record.proposal_amount || '')
            setDialogOpen(true)
        } else {
            handleStatusChange(endpoint)
        }
    }

    const handleSaveAction = async () => {
        if (selectedAction === 'mark_contacted') {
            try {
                const token = localStorage.getItem('token')
                await fetch(`${API_URL}/leads/${record.id}/mark_contacted`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ contacted_at: contactDate ? new Date(contactDate).toISOString() : null }),
                })
                // Add note about the contact method and date
                if (contactNote || contactMethod) {
                    const noteText = `Contacted via ${contactMethod}${contactNote ? ': ' + contactNote : ''}`
                    await fetch(`${API_URL}/leads/${record.id}/notes`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ content: noteText }),
                    })
                }
                notify('Lead marked as contacted', { type: 'success' })
                setDialogOpen(false)
                setSelectedAction(null)
                refresh()
            } catch (e) {
                notify('Error: ' + e.message, { type: 'error' })
            }
        } else if (selectedAction === 'mark_meeting_scheduled') {
            handleStatusChange(selectedAction, {
                meeting_scheduled_date: meetingDate,
            })
        } else if (selectedAction === 'mark_proposal_sent') {
            handleStatusChange(selectedAction, {
                proposal_amount: parseFloat(proposalAmount) || null,
            })
        }
    }

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {actions.map((action) => (
                        <Button
                            key={action.endpoint}
                            variant="contained"
                            color={action.color}
                            onClick={() => handleActionClick(action.endpoint)}
                            disabled={loading}
                            size="small"
                        >
                            {loading ? 'Updating...' : action.label}
                        </Button>
                    ))}
                </Box>

                {/* Dialog for Mark as Contacted */}
                <Dialog open={dialogOpen && selectedAction === 'mark_contacted'} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Mark as Contacted</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact Date</Typography>
                                <input
                                    type="datetime-local"
                                    value={contactDate}
                                    onChange={(e) => setContactDate(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Contact Method</Typography>
                                <select
                                    value={contactMethod}
                                    onChange={(e) => setContactMethod(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                >
                                    <option value="email">Email (Manual)</option>
                                    <option value="phone">Phone Call</option>
                                    <option value="meeting">Meeting</option>
                                    <option value="linkedin">LinkedIn</option>
                                    <option value="kernel">Email (Kernel System)</option>
                                    <option value="lemlist">Email (Lemlist)</option>
                                    <option value="other">Other</option>
                                </select>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Note (Optional)</Typography>
                                <textarea
                                    value={contactNote}
                                    onChange={(e) => setContactNote(e.target.value)}
                                    placeholder="Add details about the contact..."
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', minHeight: '80px', fontFamily: 'inherit' }}
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSaveAction} disabled={loading}>
                            {loading ? 'Saving...' : 'Mark as Contacted'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog for Schedule Meeting */}
                <Dialog open={dialogOpen && selectedAction === 'mark_meeting_scheduled'} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Schedule Meeting</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Meeting Date & Time</Typography>
                                <input
                                    type="datetime-local"
                                    value={meetingDate}
                                    onChange={(e) => setMeetingDate(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSaveAction} disabled={loading}>
                            {loading ? 'Saving...' : 'Schedule'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog for Send Proposal */}
                <Dialog open={dialogOpen && selectedAction === 'mark_proposal_sent'} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Send Proposal</DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Proposal Amount</Typography>
                                <input
                                    type="number"
                                    value={proposalAmount}
                                    onChange={(e) => setProposalAmount(e.target.value)}
                                    placeholder="Enter amount"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSaveAction} disabled={loading}>
                            {loading ? 'Saving...' : 'Send'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    )
}

export default QuickActions
